import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { translations } from "@/i18n";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Biography from "@/components/Biography";
import Ventures from "@/components/Ventures";
import Book from "@/components/Book";
import VivaArtist from "@/components/VivaArtist";
import Gallery from "@/components/Gallery";
import Publications from "@/components/Publications";
import Inventions from "@/components/Inventions";
import Fetes from "@/components/Fetes";
import Manifesto from "@/components/Manifesto";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import LecturePage from "@/pages/LecturePage";
import IngeniaPage from "@/pages/IngeniaPage";
import ArtistePage from "@/pages/ArtistePage";

export const LangContext = createContext(null);
export const lenisRef = { current: null };

export const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const useLang = () => useContext(LangContext);

export const usePageMeta = (title, description, path) => {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", description);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", description);
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement("meta");
      ogUrl.setAttribute("property", "og:url");
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute("content", `https://gillesdechassey.fr${path}`);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://gillesdechassey.fr${path}`);
  }, [title, description, path]);
};

const Home = ({ onOrder, presetSubject }) => {
  const { t } = useLang();
  usePageMeta(
    `Gilles de Chassey — ${t.hero.overline}`,
    `${t.hero.intro} ${t.book.title} — ${t.book.badge}. ${t.ventures.title}.`,
    "/"
  );
  return (
  <main>
    <Hero />
    <Marquee />
    <Biography />
    <Ventures />
    <Book onOrder={onOrder} />
    <VivaArtist />
    <Gallery />
    <Publications />
    <Inventions />
    <Fetes />
    <Manifesto />
    <Contact presetSubject={presetSubject} />
    <Footer />
  </main>
  );
};

function App() {
  const [lang, setLang] = useState(() => localStorage.getItem("gdc-lang") || "fr");
  const [presetSubject, setPresetSubject] = useState(null);

  useEffect(() => {
    localStorage.setItem("gdc-lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const t = useMemo(() => translations[lang] || translations.fr, [lang]);
  const onOrder = () => {
    setPresetSubject(t.contact.subjects[1]);
    scrollTo("contact");
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      <BrowserRouter>
        <div className="grain min-h-screen bg-paper text-ink">
          <Header />
          <Routes>
            <Route path="/" element={<Home onOrder={onOrder} presetSubject={presetSubject} />} />
            <Route path="/lecture" element={<LecturePage />} />
            <Route path="/ingenia" element={<IngeniaPage />} />
            <Route path="/artiste/:slug" element={<ArtistePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster
            position="bottom-center"
            toastOptions={{
              style: { background: "#211A13", color: "#F7F2E9", border: "1px solid #A6802E", borderRadius: 0 },
            }}
          />
        </div>
      </BrowserRouter>
    </LangContext.Provider>
  );
}

export default App;
